import React from "react";

/**
 * The selected day's log ("Wellness Day Detail", size md, no log link). Day
 * notes and the "Not rated" line are not shown: the API stores neither
 * (gaps G4, G5).
 *
 * @param {Object} props
 * @param {Object} props.detail - From core/model/calendar.dayDetail. An
 *   optional `hasItems` block (one card per item, e.g. a bowel movement) shows under the rows.
 * @param {string} [props.title]
 */
const DayDetail = ({ detail: d, title = "DAILY LOG" }) => (
  <div className="flex flex-col min-w-0">
    <div className="px-[18px] py-[16px] flex flex-col gap-[12px] border-b border-[#F1ECE8]">
      <div className="flex justify-between items-start gap-[12px]">
        <div className="flex flex-col gap-[3px] min-w-0">
          <span className="text-[10px] font-bold tracking-[.08em] text-[#8a6a58]">{title}</span>
          <span className="text-[16px] font-bold tracking-[-0.01em]">{d.date}</span>
          <span className="text-[12px] text-[#6b6b6b]">{d.sub}</span>
        </div>
        <div className="flex flex-col items-end gap-[2px] text-right">
          <span className="text-[10px] font-bold tracking-[.08em] text-[#8a6a58]">{d.valueLabel}</span>
          <span className="flex items-baseline gap-[2px]">
            <span className="text-[32px] font-bold tracking-[-0.02em] leading-none tabular-nums" style={{ color: d.valueFg }}>
              {d.value}
            </span>
            <span className="text-[12px] text-[#8a8a8a]">{d.unit}</span>
          </span>
        </div>
      </div>
      <div className="flex flex-col gap-[6px]">
        <span className="flex items-center gap-[8px] text-[12px] font-semibold text-[#3a3a3a]">
          <span className="w-[20px] h-[3px] rounded-[2px] flex-none" style={{ background: d.ctxBg, boxShadow: d.ctxRing }} />
          {d.ctx}
        </span>
        <span className="text-[12px] text-[#555] bg-[#FAF7F5] rounded-[6px] px-[9px] py-[6px] self-start">{d.state}</span>
      </div>
    </div>

    {d.hasRows && (
      <div className="px-[18px] py-[16px] flex flex-col gap-[9px]">
        <span className="text-[10px] font-bold tracking-[.08em] text-[#8a6a58]">{d.rowsTitle}</span>
        {d.rows.map((r) => (
          <div key={r.name} className="grid grid-cols-[minmax(0,118px)_minmax(0,1fr)_34px] items-center gap-[10px] text-[13px]">
            <span className="whitespace-nowrap overflow-hidden text-ellipsis">{r.name}</span>
            <span className="h-[7px] rounded-[4px] bg-[#F5F0EC] overflow-hidden block" style={{ boxShadow: r.ring }}>
              <span className="block h-full rounded-[4px]" style={{ width: r.w, background: r.bg }} />
            </span>
            <span className="text-right font-bold tabular-nums">{r.txt}</span>
          </div>
        ))}
      </div>
    )}

    {d.hasFacts && (
      <div className="px-[18px] py-[16px] flex flex-col gap-[8px]">
        {d.facts.map((f) => (
          <div key={f.k} className="flex justify-between gap-[12px] text-[13px]">
            <span className="text-[#6b6b6b]">{f.k}</span>
            <span className="font-semibold text-right">{f.v}</span>
          </div>
        ))}
      </div>
    )}

    {d.hasItems && (
      <div className="px-[18px] py-[16px] flex flex-col gap-[9px]">
        <span id="day-detail-items" className="text-[10px] font-bold tracking-[.08em] text-[#8a6a58]">{d.itemsTitle}</span>
        <ul aria-labelledby="day-detail-items" className="flex flex-col gap-[9px]">
        {d.items.map((it, i) => (
          <li key={`${it.time}-${i}`} className="flex flex-col gap-[6px] rounded-[8px] bg-[#FAF7F5] px-[10px] py-[8px]">
            <div className="flex items-center justify-between gap-[8px]">
              <span className="flex items-center gap-[8px] min-w-0">
                <span className="text-[12px] text-[#6b6b6b] tabular-nums">{it.time}</span>
                <span className="w-[10px] h-[10px] rounded-[3px] flex-none" style={{ background: it.typeBg }} />
                <span className="text-[13px] font-bold whitespace-nowrap">{it.title}</span>
                {it.chip && (
                  <span className="text-[10px] font-bold rounded-[10px] px-[7px] py-[1px]" style={{ background: it.chipBg, color: it.chipFg }}>
                    {it.chip}
                  </span>
                )}
              </span>
              <span
                className="text-[10px] font-bold rounded-[10px] px-[7px] py-[1px] whitespace-nowrap"
                style={{ background: it.statusBg, color: it.statusFg }}
              >
                {it.status}
              </span>
            </div>
            <div className="flex flex-wrap gap-x-[14px] gap-y-[2px] text-[12px]">
              {it.meta.map((m) => (
                <span key={m.k}>
                  <span className="text-[#6b6b6b]">{m.k}</span> <span className="font-semibold tabular-nums">{m.v}</span>
                </span>
              ))}
            </div>
          </li>
        ))}
        </ul>
      </div>
    )}

    {d.foot && (
      <div className="px-[18px] py-[10px] border-t border-[#F1ECE8] flex justify-between items-center gap-[10px]">
        <span className="text-[11px] text-[#8a8a8a]">{d.foot}</span>
      </div>
    )}
  </div>
);

export default DayDetail;
